import styled from '@emotion/styled';
import { Grid, Paper, Typography } from '@material-ui/core';
import { createElement, FC, Fragment } from 'react';

import { ProjectDetailsFragment, ProjectDetailsWithoutPricingDataFragment } from '../../../../../generated/graphql';
import {
  AddOriginalFormatsButton,
  AddResizeFormatsButton,
} from '../../../../component/compound/BasicProjectSettingEditor';
import _CreativeFormatCount from '../../../../component/compound/BasicProjectSettingEditor/CreativeFormatCount';
import FormatSelectBlock from '../../../../component/compound/BasicProjectSettingEditor/FormatSelectBlock';
import SectionHeader from '../../../../component/compound/BasicProjectSettingEditor/SectionHeader';
import { SPACING_HUGE, SPACING_LARGE, SPACING_REGULAR } from '../../../../constant/spacing';
import { InputtedCreationSet } from '../../../../types/InputtedCreationSet';
import { InputtedCreativeFormat } from '../../../../types/InputtedCreativeFormat';
import { Region } from '../../../../types/Region';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';
import { CreationSetAmountInput } from './CreationSetAmountInput';
import CreationSetCore from './CreationSetCore';

type Props = {
  allCreativeFormats: ReadonlyArray<InputtedCreativeFormat>;
  onlyCompetition: boolean;
  onClickAddOriginalFormatsButton: () => void;
  onClickAddResizeFormatsButton: (originalCreativeFormat: InputtedCreativeFormat) => void;
  onClickDeleteCreativeFormatButton: ({ creativeFormatId }: { creativeFormatId: string }) => void;
  region: Region;
  creationSet: InputtedCreationSet;
  project: ProjectDetailsFragment | ProjectDetailsWithoutPricingDataFragment | null;
};

// CreationSet群も一つのオーガニズムとして扱ってもいい規模だと感じたので、オーガニズム扱いする
const CreationSet: FC<Props> = ({
  allCreativeFormats,
  onlyCompetition,
  onClickAddOriginalFormatsButton,
  onClickAddResizeFormatsButton,
  onClickDeleteCreativeFormatButton,
  region,
  creationSet,
  project,
}) => {
  const { updateCreativeFormatAspectRatioDisplaySize } = useEditingProjectState();

  // ResizeBaseIdが存在しないCreativeFormatをOriginalとして抽出する <- コメントが有ることでコードがかなり読みやすい。ありがとうございます！
  // TODO: コンポーネントの表示・非表示のロジックを抽出できればより分かりやすいコードになるかも...
  const originalCreativeFormats = allCreativeFormats.filter((format) => !format.resizeBaseId);

  return (
    <>
      <CreationSetsField>
        {/* CreationSets が2つ以上存在する場合は見出しを追加(v1プロジェクト向け)*/}
        <>
          <CreationSetCount variant="h6">セット</CreationSetCount>
          <CreativeFormatCount count={allCreativeFormats.length} />
        </>

        <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={2}>
          {originalCreativeFormats.map((originalCreativeFormat, originalCreativeFormatIndex) => {
            const resizeCreativeFormats =
              originalCreativeFormat.id || originalCreativeFormat.tmpid
                ? allCreativeFormats.filter((format) => {
                    const originalId = originalCreativeFormat.id || originalCreativeFormat.tmpid;
                    return format.resizeBaseId !== null && format.resizeBaseId === originalId;
                  })
                : null;

            return (
              <CreationSetCore
                key={`CreativeFormat-${originalCreativeFormatIndex}`}
                creativeFormat={originalCreativeFormat}
                onSubmitEditingAspectRatio={updateCreativeFormatAspectRatioDisplaySize}
                onClickDeleteButton={onClickDeleteCreativeFormatButton}
                resizeCreativeFormats={resizeCreativeFormats}
                onClick={() => onClickAddResizeFormatsButton(originalCreativeFormat)}
              />
            );
          })}
          {!onlyCompetition && (
            <>
              <FormatSelectBlock
                titleMessageId={'project_editing_page.basic_setting.creative_formats.original'}
                gridItemLg={5}
              >
                <AddOriginalFormatsButton onClick={onClickAddOriginalFormatsButton} />
              </FormatSelectBlock>
              <FormatSelectBlock titleMessageId={'project_editing_page.basic_setting.creative_formats.resize'}>
                <AddResizeFormatsButton disabled onClick={() => {}} />
              </FormatSelectBlock>
            </>
          )}
        </Grid>
      </CreationSetsField>

      {/* Variation */}
      <SectionHeader
        titleMessageId={'project_editing_page.basic_setting.creative_formats.variations'}
        supportingTextMessageId={'project_editing_page.basic_setting.creative_formats.variations_description'}
        region={region}
        jpOnly
      />
      <Container>
        <CreationSetAmountInput project={project} creationSetAmount={creationSet.amount} />
      </Container>
    </>
  );
};

const Container = styled(Paper)`
  margin-bottom: ${SPACING_HUGE}px;
  padding: ${SPACING_REGULAR}px;
`;

const CreationSetsField = styled.div`
  margin-bottom: ${SPACING_LARGE}px;
`;

const CreationSetCount = styled(Typography)`
  margin: ${SPACING_REGULAR}px 0;
  font-weight: bold;
`;

const CreativeFormatCount = styled(_CreativeFormatCount)`
  margin-bottom: ${SPACING_REGULAR}px;
`;

export default CreationSet;
